import { IUnifiedCampaign } from "@/@types/campaign";
import { ErrorBoundary } from "@/components/error-boundary";
import { getUnifiedCampaignsColumns } from "@/components/campaigns/unified-campaigns-columns";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { useGetUnifiedCampaignsQuery } from "@/services/campaign";
import { ContentContainer, PageLayout, PageTitle } from "components/layout";
import { Load } from "components/load";
import PageError from "components/page-error";
import { useNavigate } from "react-router-dom";
import { DataTable } from "./components/data-table";
import { useFilterState } from "./ducks/filter/hooks/filterState";
import { CampaignAuditHistoryContent } from "./components/campaign-audit-history-content";
import { UnifiedCampaignDetailsContent } from "./components/unified-campaign-details-content";
import { EditCampaignModal } from "./components/edit-campaign";
import { ExtendCampaignModal } from "./components/extend-campaign-modal";
import { EndCampaignModal } from "./components/end-campaign-modal";
import { CampaignTypeValue } from "./components/edit-campaign/constants";
import { CampaignType } from "@/@types/campaign";
import { getTranslatedErrorMessage } from "./utils/error-messages";

const SEARCH_TYPE = {
  SKU: "sku",
  CAMPAIGN_CODE: "campaignCode",
  DEPARTMENT: "department",
  NAME: "name",
} as const;

type SearchType = (typeof SEARCH_TYPE)[keyof typeof SEARCH_TYPE];

const SEARCH_PARAM_BUILDERS: Record<SearchType, (value: string) => Record<string, string>> = {
  [SEARCH_TYPE.SKU]: (value) => ({ itemCode: value }),
  [SEARCH_TYPE.CAMPAIGN_CODE]: (value) => ({ campaignCode: value }),
  [SEARCH_TYPE.DEPARTMENT]: (value) => ({ departmentCode: value }),
  [SEARCH_TYPE.NAME]: (value) => ({ search: value }),
};

export function CampaignsList() {
  const { handleModal } = useDialogModal();
  const navigate = useNavigate();
  const { filters } = useFilterState();

  const searchType: SearchType = (filters.searchType as SearchType) || SEARCH_TYPE.NAME;
  const searchValue = filters.search || "";

  const getSearchParams = (): Record<string, string> => {
    if (!searchValue) return {};
    const builder = SEARCH_PARAM_BUILDERS[searchType] ?? SEARCH_PARAM_BUILDERS[SEARCH_TYPE.NAME];
    return builder(searchValue);
  };

  const queryParams = {
    meta: { page: filters.meta.page, perPage: filters.meta.perPage },
    campaignTypes: filters.campaignTypes || [CampaignType.PRO013],
    ...(filters.startDateFrom && { startDateFrom: filters.startDateFrom }),
    ...(filters.endDateTo && { endDateTo: filters.endDateTo }),
    ...(filters.inEffect && { inEffect: filters.inEffect }),
    ...(filters.stores && filters.stores.length > 0 && { storeCodes: filters.stores }),
    ...getSearchParams(),
  };

  const { data: response, isLoading, isFetching, error, refetch } = useGetUnifiedCampaignsQuery(queryParams);
  const { data = [], meta } = response || {};
  const countByType = meta?.countByType;

  const handleViewDetails = (campaign: IUnifiedCampaign) => {
    handleModal({
      isOpen: true,
      element: <UnifiedCampaignDetailsContent campaignCode={`${campaign.type}-${campaign.code}`} />,
      title: campaign.description,
    });
  };

  const handleEdit = (campaign: IUnifiedCampaign) => {
    handleModal({
      isOpen: true,
      element: (
        <EditCampaignModal
          campaignCode={campaign.code}
          campaignType={campaign.type as CampaignTypeValue}
        />
      ),
      title: `Editar Campanha ${campaign.type} #${campaign.code}`,
      description: campaign.description,
    });
  };

  const handleViewProducts = (campaign: IUnifiedCampaign) => {
    navigate(`/planejamento-comercial/gestao-campanhas/${campaign.type.toLowerCase()}/${campaign.code}/produtos`);
  };

  const handleExtend = (campaign: IUnifiedCampaign) => {
    handleModal({
      isOpen: true,
      element: (
        <ExtendCampaignModal
          campaignCode={campaign.code}
          campaignType={campaign.type as CampaignTypeValue}
          campaignDescription={campaign.description}
          currentEndDate={campaign.endDate}
        />
      ),
      title: `Prorrogar Campanha ${campaign.type} #${campaign.code}`,
      description: campaign.description,
    });
  };

  const handleEnd = (campaign: IUnifiedCampaign): void => {
    handleModal({
      isOpen: true,
      element: (
        <EndCampaignModal
          campaignCode={campaign.code}
          campaignType={campaign.type}
          startDate={campaign.startDate}
          currentEndDate={campaign.endDate}
        />
      ),
      title: `Encerrar campanha ${campaign.type} #${campaign.code}`,
      description: campaign.description,
    });
  };

  const handleViewHistory = (campaign: IUnifiedCampaign) => {
    // Audit log existe pra PRO013/14/17 (PRO012 é legado read-only).
    const auditableType =
      campaign.type === CampaignType.PRO013 ||
      campaign.type === CampaignType.PRO014 ||
      campaign.type === CampaignType.PRO017;
    if (!auditableType) return;
    handleModal({
      isOpen: true,
      element: (
        <CampaignAuditHistoryContent
          type={campaign.type as "PRO013" | "PRO014" | "PRO017"}
          code={campaign.code}
        />
      ),
      title: `Histórico — ${campaign.type} #${campaign.code}`,
      description: campaign.description,
    });
  };

  const columns = getUnifiedCampaignsColumns({
    onViewDetails: handleViewDetails,
    onEdit: handleEdit,
    onViewProducts: handleViewProducts,
    onExtend: handleExtend,
    onViewHistory: handleViewHistory,
    onEnd: handleEnd,
  });

  if (isLoading) return <Load />;
  if (error) {
    const errorMessage = getTranslatedErrorMessage(
      error,
      "Ocorreu um erro ao carregar as campanhas. Tente novamente mais tarde."
    );
    return <PageError status={500} message={errorMessage} />;
  }

  return ( 
    <ErrorBoundary>
      <PageLayout>
        <PageTitle
          title="Gestão de Campanhas"
          description="Gerencie todas as campanhas promocionais em um único lugar"
        />
        <ContentContainer>
          <DataTable
            data={data}
            meta={meta}
            columns={columns}
            countByType={countByType}
            onRefresh={refetch}
            isRefreshing={isFetching && !isLoading}
          />
        </ContentContainer>
      </PageLayout>
    </ErrorBoundary>
  );
}
